import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { TicketActorType, type TicketMessageDto, TicketMessageKind, type TicketTimelineEntryDto, TicketTimelineEntryKind, useTicketApi } from "@/api/tickets";
import { useSession } from "@/auth/session-provider";
import { useI18n } from "@/locales/i18n-provider";
import { toActivityItem } from "../utils/ticket-activity";
import type { TicketAttachmentView } from "../utils/ticket-attachments";
import { renderMentionTokens } from "../utils/ticket-mentions";
import type { TicketActivityItem } from "../utils/ticket-presentation";

type PendingNote = Extract<TicketActivityItem, { kind: "note" }> & { ticketId: string | undefined };
const sentStatusDurationMs = 2000;

function deliveryMessageKey(ticketId: string | undefined, messageId: string): string {
  return `${ticketId ?? ""}:${messageId}`;
}

/** 编辑器提交时一并带上的提及，乐观渲染直接用，不必等服务端解析。 */
export type AddNoteInput = {
  /** 已上传附件的 id；附件在编辑器里加入时就传好了，这里只是把它们钉到这条消息上。 */
  attachmentIds: string[];
  /** 同一批附件的本地展示模型，供乐观渲染用。 */
  attachments: TicketAttachmentView[];
  body: string;
  mentions: Array<{ agentId: string; displayName: string }>;
};

/** 需要纯文本时将 `@{agentId}` 换回显示名；交互式 timeline 会保留 token 以绑定准确身份。 */
export function renderMentions(message: TicketMessageDto): string {
  return renderMentionTokens(message.body, new Map(message.mentions.map((mention) => [mention.agentId, mention.displayName])));
}

function messageToTimelineEntry(message: TicketMessageDto): TicketTimelineEntryDto {
  return {
    id: `message-${message.id}`,
    kind: TicketTimelineEntryKind.Message,
    occurredAt: message.creationTime,
    actorType: TicketActorType.Agent,
    actorId: message.authorId,
    actorDisplayName: message.authorDisplayName,
    actorAvatarUrl: message.authorAvatarUrl,
    action: null,
    fromValue: null,
    toValue: null,
    fromDisplayName: null,
    toDisplayName: null,
    detailJson: null,
    message,
  };
}

export function useTicketDetail(ticketId: string | undefined) {
  const { intlLocale, t } = useI18n();
  const ticketApi = useTicketApi();
  const queryClient = useQueryClient();
  const { agent } = useSession();
  const [pendingNotes, setPendingNotes] = useState<PendingNote[]>([]);
  const [sentMessageKeys, setSentMessageKeys] = useState<Set<string>>(new Set());
  const ticketQueryKey = ["tickets", "detail", ticketId] as const;
  const timelineQueryKey = ["tickets", "timeline", ticketId] as const;
  const attachmentsQueryKey = ["tickets", "attachments", ticketId] as const;

  const ticketQuery = useQuery({
    queryKey: ticketQueryKey,
    queryFn: ({ signal }) => ticketApi.get(ticketId as string, signal),
    enabled: Boolean(ticketId),
  });

  // 事件与消息由服务端归并成一条有序流，前端不再单独取 messages。
  const timelineQuery = useQuery({
    queryKey: timelineQueryKey,
    queryFn: ({ signal }) => ticketApi.getTimeline(ticketId as string, signal),
    enabled: Boolean(ticketId),
  });

  const serverActivities = useMemo<TicketActivityItem[]>(
    () => (timelineQuery.data ?? []).map((entry) => toActivityItem(entry, t, intlLocale)),
    [intlLocale, t, timelineQuery.data],
  );
  const activities = useMemo<TicketActivityItem[]>(
    () => [
      ...serverActivities.map((activity) =>
        activity.kind === "note" && sentMessageKeys.has(deliveryMessageKey(ticketId, activity.id)) ? { ...activity, deliveryState: "sent" as const } : activity,
      ),
      ...pendingNotes.filter((note) => note.ticketId === ticketId).map((note) => ({ ...note, time: t("tickets.time.justNow") })),
    ],
    [serverActivities, pendingNotes, sentMessageKeys, t, ticketId],
  );

  const addNote = useMutation({
    mutationFn: ({ body, attachmentIds }: AddNoteInput) =>
      ticketApi.addMessage(ticketId as string, { kind: TicketMessageKind.InternalNote, body, attachmentIds }),
    onMutate: ({ body, mentions, attachments }) => {
      const clientId = `pending-note-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      setPendingNotes((current) => [
        ...current,
        {
          id: clientId,
          kind: "note",
          author: agent.displayName,
          avatarUrl: agent.avatarUrl,
          initials: agent.displayName.slice(0, 1),
          content: body,
          mentions,
          // 附件已经传完了，乐观渲染直接用本地文件，省一次回源。
          attachments,
          time: "",
          deliveryState: "sending",
          ticketId,
        },
      ]);
      return { clientId, ticketId };
    },
    onSuccess: (message, _body, context) => {
      const timelineEntry = messageToTimelineEntry(message);
      queryClient.setQueryData<TicketTimelineEntryDto[]>(timelineQueryKey, (current = []) => {
        if (current.some((entry) => entry.message?.id === message.id)) return current;
        return [...current, timelineEntry];
      });
      setPendingNotes((current) => current.filter((note) => note.id !== context.clientId));
      const sentMessageKey = deliveryMessageKey(context.ticketId, timelineEntry.id);
      setSentMessageKeys((current) => new Set(current).add(sentMessageKey));
      window.setTimeout(() => {
        setSentMessageKeys((current) => {
          const next = new Set(current);
          next.delete(sentMessageKey);
          return next;
        });
      }, sentStatusDurationMs);

      // 服务端会补上 authorDisplayName 与提及解析，重新拉一遍比本地拼更靠谱；
      // 工单本身也要刷——加消息会带出 message_added 事件和首次响应时间。
      void queryClient.invalidateQueries({ queryKey: timelineQueryKey });
      void queryClient.invalidateQueries({ queryKey: ticketQueryKey });
      void queryClient.invalidateQueries({ queryKey: ["tickets", "list"] });
      // 刚发出的附件此刻才从草稿变成正式附件，「附件」页签要跟着更新。
      if (message.attachments.length > 0) {
        void queryClient.invalidateQueries({ queryKey: attachmentsQueryKey });
      }
    },
    onError: (_error, _body, context) =>
      setPendingNotes((current) => current.map((note) => (note.id === context?.clientId ? { ...note, deliveryState: "failed" } : note))),
  });

  return {
    ticketQuery,
    timelineQuery,
    ticket: ticketQuery.data,
    activities,
    addNote,
  };
}
